import { EventEmitter } from 'events';
import { ethers } from 'ethers';

import CoinTools from '../tools/coin_tools';
import { isValidChain, isSameAddress } from '../tools/eth_tools';
import Storage from '../tools/storage';
import util from '../tools/util';

import WalletStore from './wallet_store';

import RAW_COINS from '../data/coins.json';

export default {
  once,
  addChangeListener,
  removeChangeListener,
  init,
  coinAmount,
  weiAmount,
  getBalance,
  getCommonCoins,
  getCoinByAddress,
  search,
  addCoin,
  fetchBalance,
};
const CHAIN_PRIORITY = {
  '137': 1,
  '100': 2,
  '56': 3,
  '1': 4,
  '1313161554': 5,
};
const STORAGE_KEY = 'harbour_swap_coins';
const COINS = RAW_COINS.filter((coin) => isValidChain(coin.chain_id));
const COMMON_COINS = COINS.filter((coin) => coin.is_common).sort(_sortCommon);

let g_customCoinList = [];
const g_balanceMap = new Map();
const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function once(callback) {
  g_eventEmitter.once(CHANGE_EVENT, callback);
}
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function init(done) {
  Storage.getItem(STORAGE_KEY, (err, list) => {
    if (!err && list && list.length > 0) {
      g_customCoinList = list;
      list.forEach((coin) => COINS.push(coin));
    }
    done && done();
  });
}
function coinAmount(amount: any, coin: any): number {
  let ret = 0;
  try {
    const s = ethers.utils.formatUnits(amount, coin.decimals);
    ret = parseFloat(s);
  } catch (e) {
    util.errorLog('CoinStore.coinAmount: threw:', e);
  }
  return ret;
}
function weiAmount(amount: number, coin: any) {
  let ret;
  try {
    ret = ethers.utils.parseUnits(String(amount), coin.decimals);
  } catch (e) {
    util.errorLog('CoinStore.weiAmount: threw:', e);
  }
  return ret;
}
function getBalance(coin: any) {
  const balance = g_balanceMap.get(coin.coin_id);
  let ret;
  if (balance) {
    ret = coinAmount(balance, coin);
  }
  return ret;
}
function getCommonCoins(chain_id?: number) {
  let ret;
  if (chain_id) {
    ret = COMMON_COINS.filter((coin) => coin.chain_id === chain_id);
  } else {
    ret = COMMON_COINS.slice();
  }
  return ret;
}
function getCoinByAddress(chain_id: number, contract_addr: string) {
  let ret = COINS.find(
    (c) =>
      c.chain_id === chain_id && isSameAddress(c.contract_addr, contract_addr)
  );
  if (!ret) {
    ret = {
      coin_id: `unknown_${chain_id}_${contract_addr}`,
      chain_id,
      contract_addr,
      symbol: '',
      name: '',
      decimals: 18,
    };
  }
  return ret;
}
function search(search_text: string, chain_id?: number) {
  const regex = new RegExp(search_text, 'i');
  let ret = COINS.filter(_filterSearch.bind(null, regex, chain_id));
  ret.sort(_sortScore);
  ret = ret.slice(0, 100);
  return ret;
}
function addCoin(coin: any) {
  coin.coin_id = 'custom_' + coin.chain_id + '_' + coin.contract_addr;
  coin.is_custom = true;
  g_customCoinList.push(coin);
  COINS.push(coin);
  Storage.setItem(STORAGE_KEY, g_customCoinList);
}
function fetchBalance(coin: any) {
  const { chain_id, contract_addr } = coin;
  const owner = WalletStore.getAddress();
  if (owner) {
    const opts = { chain_id, contract_addr, owner };
    CoinTools.balanceOf(opts, (err, balance) => {
      if (!err) {
        g_balanceMap.set(coin.coin_id, balance);
        _emit('balance');
      }
    });
  } else {
    g_balanceMap.clear();
    _emit('balance');
  }
}

function _filterSearch(regex, chain_id, coin) {
  if (chain_id && chain_id !== coin.chain_id) {
    return false;
  }
  let score = 0;
  const symbol_match = coin.symbol.match(regex);
  if (symbol_match) {
    if (coin.is_custom) {
      score = 400;
    } else if (symbol_match[0] === coin.symbol) {
      score = 300;
    } else if (symbol_match.index === 0) {
      score = 200;
    } else {
      score = 100 - symbol_match.index;
    }
    coin.search_score = score;
    return true;
  }
  const name_match = coin.name.match(regex);
  if (name_match) {
    if (coin.is_custom) {
      score = 400;
    } else if (name_match[0] === coin.name) {
      score = 3;
    } else if (name_match.index === 0) {
      score = 2;
    } else {
      score = 1;
    }
    coin.search_score = score;
    return true;
  }
  return false;
}
function _sortScore(a, b) {
  if (a.search_score !== b.search_score) {
    return b.search_score - a.search_score;
  } else if (a.is_common !== b.is_common) {
    return b.is_common - a.is_common;
  } else {
    return _sortCommon(a, b);
  }
}
function _sortCommon(a, b) {
  if (a.chain_id === b.chain_id) {
    return a.symbol.localeCompare(b.symbol);
  } else {
    const a_prio = CHAIN_PRIORITY[a.chain_id];
    const b_prio = CHAIN_PRIORITY[b.chain_id];
    return a_prio - b_prio;
  }
}
